/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable import/no-named-as-default */
import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { any, bool, func, object, objectOf, oneOf, oneOfType, shape, string } from 'prop-types';

import FeatureFlag from 'yoda-core-components/lib/components/FeatureFlag/FeatureFlag';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import IrisActions from 'yoda-site-components/lib/actions/IrisAction';
import IrisPage from 'yoda-site-components/lib/components/IrisPage/IrisPage';
import Layout from 'yoda-site-components/lib/components/Layout/Layout';
import ScrollTop from 'yoda-site-components/lib/components/ScrollTop/ScrollTop';
import irisComponentsForGallery from 'yoda-site-components/lib/iris/components/forGallery';
import { miniPDPOnClose } from 'yoda-site-components/lib/actions/MiniPDPAction';
import { selectShouldRender } from 'yoda-site-components/lib/selectors/ContextSelector';

import MiniPDPInterstitialAsync from 'yoda-mini-pdp/lib/components/MiniPDPInterstitial/MiniPDPInterstitialAsync';

import { getGalleryDetailsById, initiatePendingActions } from '../../../actions/GalleryAction';
import {
    getGalleryContentWithIris,
    setInitialIrisRequestMode,
} from '../../../actions/GalleryIrisAction';

import { patterns } from '../../../configurations/config';

import { loadGalleryDetails } from '../../../helpers/GalleryHelper';
import {
    galleryPostProcessorForIris,
    getIrisParamForGallery,
    loadIrisGalleryContent,
} from '../../../helpers/IrisApiCallingFunctions';
import redirectRequired from '../../../helpers/RedirectHelper';

import { getIrisFallBackStatus } from '../../../selectors/IrisFailure';
import {
    selectIrisContentCallLoading,
    selectIrisContentCallStatus,
} from '../../../selectors/fromState';
import GalleryLoader from '../../../components/GalleryLoader/GalleryLoader';
import {
    addToRecentSearch,
    compareUrls,
    isClientSideRenderingEnabled,
    isContentHydratedWithIris,
    isFirstPageWithoutPrev,
    getURLParameterBykey,
    isHydrated,
    updateUrl,
} from '../../../utils';
import SeoSchema from '../../../components/SeoSchema/SeoSchema';
import StoreAnalytics from '../../../components/StoreAnalytics/StoreAnalytics';
import TimeoutError from '../../../components/TimeoutError/TimeoutError';
import FailOverLayout from '../../../components/FailOverLayout/FailOverLayout';

import gridZoneRender from '../../../iris/zoneOverrides/grid';
import aboveTheGridZoneOverride from '../../../iris/zoneOverrides/aboveTheGrid';
import irisPageSpecificComponents from '../../../iris/components';

import { fail } from './constants';

/**
 * IRIS integration wiki: https://wiki.jcpenney.com/pages/viewpage.action?pageId=79664545
 * Please update if logic changes are made in this file.
 */
/* istanbul ignore next */
export class IrisWrapper extends Component {
    static propTypes = {
        actions: objectOf(func).isRequired,
        commonData: oneOfType([bool, object, string]).isRequired,
        deviceType: objectOf(bool).isRequired,
        featureFlags: objectOf(bool).isRequired,
        enableMultiSelectFilters: bool.isRequired,
        irisCallStatus: oneOfType([bool, string]).isRequired,
        irisFallBackStatus: bool.isRequired,
        location: objectOf(any).isRequired,
        pageType: oneOf(['Gallery', 'Search']).isRequired,
        refreshContent: bool.isRequired,
        showBackToTop: bool.isRequired,
        v1patterns: shape({ gallery: shape({ flags: string, pattern: string }) }).isRequired,
        cookies: shape({ DPCPT: string }),
        hostname: string,
        irisContentCallCompleted: bool,
        shouldRender: bool,
        solarCallStatus: bool,
        irisData: objectOf(any),
    };

    static defaultProps = {
        cookies: {},
        hostname: '',
        irisContentCallCompleted: false,
        shouldRender: true,
        solarCallStatus: true,
        irisData: {},
    };

    constructor(props) {
        super(props);
        this.postProcesser = galleryPostProcessorForIris.bind({
            getGalleryContentWithIris: props.actions.getGalleryContentWithIris,
        });
        this.isGalleryPage = props.pageType === 'Gallery';
        this.isSearchPage = props.pageType === 'Search';
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        const {
            actions,
            commonData,
            cookies: { DPCPT = '' } = {},
            deviceType: { isBot },
            featureFlags,
            location,
            v1patterns,
        } = this.props;

        const { enableSSRForPLP, enableV1Redirect } = featureFlags;

        const redirectStatus = redirectRequired(commonData, location.pathname);
        let invokeDetailsFromClient = isClientSideRenderingEnabled(featureFlags) && !isBot;
        let invokedIrisClientSide = false;
        const isFirstPage = isFirstPageWithoutPrev();
        const marketingTilesCount = isFirstPage ? 0 : getURLParameterBykey('mktTiles');

        if (__SERVER__) {
            if (enableV1Redirect) {
                const { gallery: { flags, pattern } = {} } = v1patterns;

                const v1GalleryPattern = new RegExp(pattern, flags);

                invokeDetailsFromClient =
                    invokeDetailsFromClient && !v1GalleryPattern.test(location.pathname);
            }

            const ssrThrottleEnabledForPLP = DPCPT.includes('CP4Y');

            /**
             * Load gallery details for the following:
             *   enableSSRForPLP is true per YODATOF-147
             *   existing v1 redirects
             */
            const shouldLoadGalleryDetails = [
                !isBot,
                !this.isSearchPage,
                enableSSRForPLP,
                ssrThrottleEnabledForPLP,
            ].every((condition) => condition);

            if (!redirectStatus) {
                if (shouldLoadGalleryDetails || !invokeDetailsFromClient) {
                    loadGalleryDetails(
                        location,
                        actions,
                        true,
                        false,
                        featureFlags,
                        marketingTilesCount,
                        invokedIrisClientSide
                    );
                }

                loadIrisGalleryContent(location, actions, true, featureFlags);
            }
        } else {
            window.jcpDLjcp = {};
            let locationHref = window.location.href;
            const invokeGalleryDetails = !isHydrated(this.props);

            // When unable to render server-side (e.g. Server Too Busy) get all content zones.
            if (!isContentHydratedWithIris(this.props.irisData)) {
                loadIrisGalleryContent(location, actions, true, featureFlags);
                invokedIrisClientSide = true;
            }

            /**
             * Load gallery details for the following:
             *   existing v1 redirects
             *   server-side failed to load
             *   server-side is disabled
             */
            if (invokeGalleryDetails || (invokeDetailsFromClient && !enableSSRForPLP)) {
                loadGalleryDetails(
                    location,
                    actions,
                    true,
                    false,
                    featureFlags,
                    marketingTilesCount,
                    invokedIrisClientSide
                );
            } else {
                actions.initiatePendingActions(this.props);
            }

            /* istanbul ignore next */
            this.browserHistoryListener = getBrowserHistory().listen((loc) => {
                const currentLocation = window.location.href;
                const { refreshContent } = this.props;
                const isReplaceAction = loc.action === 'REPLACE';
                const isCurrentLocation = locationHref === currentLocation;
                const marketingTileCount = getURLParameterBykey('mktTiles') || 0;

                if (!isReplaceAction && (!isCurrentLocation || refreshContent)) {
                    locationHref = currentLocation;
                    loadIrisGalleryContent(loc, actions, false, featureFlags);

                    if (this.isGalleryPage) {
                        loadGalleryDetails(
                            loc,
                            actions,
                            undefined,
                            false,
                            featureFlags,
                            marketingTileCount,
                            true
                        );
                    } else if (this.isSearchPage) {
                        loadGalleryDetails(
                            loc,
                            actions,
                            false,
                            false,
                            featureFlags,
                            marketingTileCount,
                            true
                        );
                    }
                }
            });
        }

        this.isSearchPage && addToRecentSearch();
    }

    shouldComponentUpdate(nextProps) {
        const {
            commonData: { seoTitleTags, seoTitleTags: { canonicalURL } = {} } = {},
            irisCallStatus,
            irisContentCallCompleted,
            location: { pathname } = {},
        } = this.props;

        const {
            commonData: {
                seoTitleTags: nextSeoTitleTags,
                seoTitleTags: { canonicalURL: nextCanonicalURL } = {},
            } = {},
            irisCallStatus: nextIrisCallStatus,
            irisContentCallCompleted: nextIrisContentCallCompleted,
        } = nextProps;

        const hasCallStatus = nextIrisCallStatus === irisCallStatus;
        const isCallCompleted = nextIrisContentCallCompleted === irisContentCallCompleted;

        /* istanbul ignore next */
        if (pathname && nextSeoTitleTags && seoTitleTags) {
            if (
                nextCanonicalURL &&
                !compareUrls(pathname, nextCanonicalURL) &&
                !compareUrls(
                    this.canonicalUrlPreviewRemove(canonicalURL),
                    this.canonicalUrlPreviewRemove(nextCanonicalURL)
                )
            ) {
                // Update URL if canonical and original don't match.
                updateUrl(nextCanonicalURL, {}, true);
            }
        }

        if (!hasCallStatus || !isCallCompleted) return true;
        return false;
    }

    canonicalUrlPreviewRemove = (url = '') => {
        const { hostname } = this.props;
        let urlReturn = url;
        if (hostname === 'preview.jcpenney.com') {
            urlReturn = url.replace(/preview\//i, '');
        }
        return urlReturn;
    };

    render() {
        const {
            enableMultiSelectFilters,
            irisCallStatus,
            irisContentCallCompleted,
            irisFallBackStatus,
            location,
            pageType,
            shouldRender,
            showBackToTop,
        } = this.props;

        const irisParams = getIrisParamForGallery(location);
        const irisPageProps = {
            aggregateAPI: true,
            'data-automation-id': pageType,
            irisClientCall: false,
            irisComponents: irisComponentsForGallery,
            irisPageSpecificComponents,
            pageType,
            params: irisParams,
            postProcessor: this.postProcesser,
            theme: 'gallery',
            zoneRenderer: [
                {
                    zone: 'ABVGRID',
                    renderer: aboveTheGridZoneOverride,
                },
                {
                    renderer: gridZoneRender,
                    zone: 'GRID',
                },
            ],
        };

        const PageLayout = (showIrisPage) => (
            <Layout>
                <>
                    {!enableMultiSelectFilters && <GalleryLoader />}
                    <SeoSchema />
                    <StoreAnalytics />
                    <TimeoutError />
                    <ScrollTop showBackToTop={showBackToTop} />
                    {showIrisPage ? (
                        <IrisPage {...irisPageProps} />
                    ) : (
                        <FailOverLayout pageType={pageType} />
                    )}
                    <FeatureFlag name="enableMiniPDPFeature">
                        <MiniPDPInterstitialAsync />
                    </FeatureFlag>
                </>
            </Layout>
        );
        if (irisFallBackStatus || (irisCallStatus === fail && irisContentCallCompleted)) {
            return PageLayout(false);
        }

        return shouldRender ? (
            PageLayout(true)
        ) : (
            <Layout>
                <IrisPage {...irisPageProps} />
            </Layout>
        );
    }
}

const mapStateToProps = (state) => {
    const {
        commonData = {},
        context: {
            deviceType = { isBot: false },
            featureFlags = {},
            hostname = '',
            featureFlags: { enableMultiSelectFilters = false, showBackToTop = false },
            headers: { cookies = {} } = {},
            preferences: { v1patterns = patterns.v1patterns } = {},
        } = {},
        refreshContent = false,
        irisData,
    } = state;

    return {
        commonData,
        cookies,
        deviceType,
        enableMultiSelectFilters,
        featureFlags,
        hostname,
        irisCallStatus: selectIrisContentCallStatus(state),
        irisContentCallCompleted: selectIrisContentCallLoading(state),
        irisFallBackStatus: getIrisFallBackStatus(state),
        refreshContent,
        shouldRender: selectShouldRender(state),
        showBackToTop,
        v1patterns,
        irisData,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            getGalleryContentWithIris, // Used in postProcesser
            getGalleryDetailsById,
            getIrisTemplateData: IrisActions.getIrisTemplateData,
            initiatePendingActions,
            miniPDPOnClose,
            setInitialIrisRequestMode, // Used for orphan products logic
        },
        dispatch
    ),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(IrisWrapper));
